import type {
  CheckoutOrderGroup,
  OrderBookingSnapshot,
  ShopFulfillmentOrder,
} from './checkoutOrderTypes';
import { aggregateCheckoutStatus } from './aggregateCheckoutStatus';
import {
  aggregateStatusLabel,
  resolveShopOrderStatus,
  shopStatusLabel,
} from './orderStatus';
import { resolveShopDisplayName } from './resolveShopDisplayName';

const FALLBACK_BUCKET_MS = 2 * 60 * 1000;

function parseTotal(value: string | number): number {
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

function checkoutGroupKey(booking: OrderBookingSnapshot): string {
  if (booking.payment_id?.trim()) {
    return `pay:${booking.payment_id.trim()}`;
  }
  const ts = new Date(booking.created_at).getTime();
  const bucket = Math.floor(ts / FALLBACK_BUCKET_MS);
  const payType = booking.payment_type?.trim() || 'unknown';
  return `fallback:${booking.user_id}:${payType}:${bucket}`;
}

function sortBookingsNewestFirst(a: OrderBookingSnapshot, b: OrderBookingSnapshot): number {
  return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
}

function toShopFulfillment(
  booking: OrderBookingSnapshot,
  shopNamesById: Record<string, string>
): ShopFulfillmentOrder {
  const shopStatus = resolveShopOrderStatus(booking);
  return {
    booking,
    shopId: booking.shop_id,
    shopName: resolveShopDisplayName(booking.shop_id, shopNamesById),
    shopStatus,
    shopStatusLabel: shopStatusLabel(shopStatus),
  };
}

function buildGroup(
  groupKey: string,
  bookings: OrderBookingSnapshot[],
  shopNamesById: Record<string, string>
): CheckoutOrderGroup {
  const sorted = [...bookings].sort(sortBookingsNewestFirst);
  const primary = sorted[0];
  const paymentId = primary.payment_id?.trim() || null;
  const shopOrders = sorted.map((b) => toShopFulfillment(b, shopNamesById));
  const aggregateStatus = aggregateCheckoutStatus(sorted);
  const totalAmount = sorted.reduce((sum, b) => sum + parseTotal(b.total), 0);

  return {
    id: groupKey,
    paymentId,
    createdAt: primary.created_at,
    aggregateStatus,
    aggregateStatusLabel: aggregateStatusLabel(aggregateStatus),
    shopOrders,
    totalAmount,
    paymentType: primary.payment_type ?? null,
    sellerCount: sorted.length,
    isMultiSellerCheckout: sorted.length > 1 && Boolean(paymentId),
  };
}

/**
 * Group flat booking rows into virtual checkout orders (Phase 1 — no DB parent table).
 */
export function groupBookingsByCheckout(
  bookings: OrderBookingSnapshot[],
  shopNamesById: Record<string, string> = {}
): CheckoutOrderGroup[] {
  if (bookings.length === 0) return [];

  const buckets = new Map<string, OrderBookingSnapshot[]>();

  for (const booking of bookings) {
    const key = checkoutGroupKey(booking);
    const list = buckets.get(key) ?? [];
    list.push(booking);
    buckets.set(key, list);
  }

  const groups = Array.from(buckets.entries()).map(([key, list]) =>
    buildGroup(key, list, shopNamesById)
  );

  groups.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return groups;
}

export function findCheckoutGroupByPaymentId(
  groups: CheckoutOrderGroup[],
  paymentId: string | null | undefined
): CheckoutOrderGroup | undefined {
  if (!paymentId?.trim()) return undefined;
  const needle = paymentId.trim();
  return groups.find((g) => g.paymentId === needle);
}

export function formatCheckoutDisplayId(group: CheckoutOrderGroup): string {
  if (group.paymentId) {
    return group.paymentId.length > 12
      ? group.paymentId.slice(0, 12)
      : group.paymentId;
  }
  return group.shopOrders[0]?.booking.id.slice(0, 8) ?? group.id.slice(0, 8);
}
